import type { AuditEntry, ChannelWrite, Group, User } from "@estelle/domain";

/** A row flattened to the field names its module declares. */
export type Projection = Record<string, unknown>;

/**
 * A user flattened for display.
 *
 * `roleIds` and `groupIds` are passed through as ids, not names — read the
 * comment on `USER_FIELDS.roleIds` in `fields.ts` for why. `UsersList`
 * resolves them to badges of the current role and group names via
 * `renderOverride`, the same way `TransfersList` overrides its `status`
 * column instead of teaching `DataTable` a second, module-specific renderer.
 */
export function projectUser(user: User): Projection {
  return {
    name: user.name,
    email: user.email,
    status: user.status,
    roleIds: user.roleIds,
    groupIds: user.groupIds,
    lastLoginAt: user.lastLoginAt,
    createdAt: user.createdAt,
  };
}

/**
 * A group flattened for display.
 *
 * Deliberately missing `memberCount` — see `GROUP_FIELDS.memberCount` in
 * `fields.ts` for why it is computed by `GroupsList` instead of here: this
 * function only has the one `Group` it was handed, and a member count needs
 * the whole user list to answer.
 */
export function projectGroup(group: Group): Projection {
  return {
    name: group.name,
    description: group.description ?? "",
    roleIds: group.roleIds,
  };
}

/**
 * An audit entry flattened for display.
 *
 * `before` and `after` are deliberately absent — they are `Json`, not a
 * displayable scalar, and `renderCell` has nothing to do with an object but
 * print `[object Object]`. `AuditLog` renders them in the detail panel
 * instead, once a row is selected, the same way `ContactList` defers a
 * contact's phones and addresses to `ContactDetail` rather than cramming
 * them into a column.
 */
export function projectAuditEntry(entry: AuditEntry): Projection {
  return {
    occurredAt: entry.occurredAt,
    actorUserId: entry.actorUserId,
    action: entry.action,
    entityType: entry.entityType,
    entityId: entry.entityId ?? "",
    ipAddress: entry.ipAddress ?? "",
  };
}

/**
 * A channel write flattened for display.
 *
 * `actorUserId` passes through as the id, not a name — `ShopifySync` resolves
 * it via `renderOverride`, the same pattern `AuditLog` uses for
 * `AuditEntry.actorUserId` above. `error` and a null `actorUserId` fall back
 * to `""` rather than `null` so `renderCell` renders the em dash it already
 * renders for any empty value, instead of `ShopifySync` needing its own
 * null-to-dash helper for a second field.
 */
export function projectChannelWrite(write: ChannelWrite): Projection {
  return {
    status: write.status,
    mutation: write.mutation,
    entityType: write.entityType,
    entityId: write.entityId,
    actorUserId: write.actorUserId ?? "",
    createdAt: write.createdAt,
    error: write.error ?? "",
  };
}
